/**
 * Context Resolver
 *
 * Resolves context IDs to full content using prefix-based routing.
 *
 * Responsibilities:
 * - Parse context ID prefix to determine storage type
 * - Route to appropriate storage backend
 * - Resolve single or multiple context IDs
 * - Store new content with generated context IDs
 *
 * Supported ID Formats:
 * 1. Simple prefixed IDs: "logo_abc123", "wikimedia_xyz78"
 * 2. Composite IDs: "deck_abc:slide_257:shape_42" (for document-inherited references)
 *    - Composite IDs extract the content type from the second segment
 *    - Example: "deck_abc:slide_257" → routes based on "slide" prefix
 *
 * Storage Strategy:
 * - In-memory (GlobalStateManager): as_, logo_, color_, wikimedia_
 * - On-device DB: slide_, component_, layout_, asset_, blueprint_
 * - RAG Vector Store: page_, excelread_
 */
import { getContextIDPrefix, generateContextID, registerUsedId, } from '../idGenerator';
import { processImage, estimateImageTokens, detectImageMimeType, } from '../utils/imageUtils';
import { isCompositeId, resolveCompositeId, } from './compositeIdUtils';
import { getAgentServices } from '../services';
// ═══════════════════════════════════════════════════════════════════════════
// STORAGE ROUTING
// ═══════════════════════════════════════════════════════════════════════════
/** Prefixes that route to in-memory session storage */
const IN_MEMORY_PREFIXES = ['as', 'logo', 'color', 'wikimedia', 'artifact', 'meta'];
/** Prefixes that route to on-device DB (presentation-scoped) */
const ON_DEVICE_PREFIXES = ['slide', 'component', 'layout', 'asset', 'blueprint'];
/** Prefixes that route to RAG vector store */
const RAG_PREFIXES = ['page', 'excelread'];
/** Prefixes that route to linked data storage */
const LINKED_PREFIXES = ['excel'];
// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT IN-MEMORY STORAGE
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Simple in-memory storage implementation
 * Used as fallback and for session-scoped data
 */
class InMemoryStorage {
    constructor() {
        this.store = new Map();
    }
    get(id) {
        return this.store.get(id)?.content;
    }
    set(id, content) {
        this.store.set(id, { content, storedAt: Date.now() });
    }
    has(id) {
        return this.store.has(id);
    }
    delete(id) {
        return this.store.delete(id);
    }
    getStoredAt(id) {
        return this.store.get(id)?.storedAt;
    }
    clear() {
        this.store.clear();
    }
    size() {
        return this.store.size;
    }
    keys() {
        return Array.from(this.store.keys());
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT RESOLVER CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Resolver for short ID to content mapping
 *
 * @example
 * const resolver = ContextResolver.getInstance();
 *
 * // Store content
 * const contextId = resolver.store('as', transcriptContent);
 * // Returns: "as_x7f9k2"
 *
 * // Resolve content
 * const resolved = resolver.resolve(contextId);
 * // Returns: { id: "as_x7f9k2", prefix: "as", content: transcriptContent }
 *
 * // Resolve context items
 * const contextItems = [
 *   { context: ["as_abc12", "logo_xyz78"], purpose: "Revenue data and branding" }
 * ];
 * const resolved = resolver.resolveContextItems(contextItems);
 *
 * Note: All IDs are called "context IDs" for consistency across the codebase.
 */
export class ContextResolver {
    constructor() {
        /** Default in-memory storage for session data */
        this.inMemoryStorage = new InMemoryStorage();
        /** Registered storage backends by prefix */
        this.storageByPrefix = new Map();
        /** Image cache (base64) - separate from content storage */
        this.imageCache = new Map();
        // Register default in-memory storage for appropriate prefixes
        for (const prefix of IN_MEMORY_PREFIXES) {
            this.storageByPrefix.set(prefix, this.inMemoryStorage);
        }
    }
    /**
     * Get singleton instance
     */
    static getInstance() {
        if (!ContextResolver.instance) {
            ContextResolver.instance = new ContextResolver();
        }
        return ContextResolver.instance;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // STORAGE REGISTRATION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Register a storage backend for specific prefixes
     *
     * @param prefixes - Array of prefixes to route to this storage
     * @param storage - Storage implementation
     *
     * @example
     * // Register on-device DB for presentation content
     * resolver.registerStorage(['slide', 'comp', 'layout'], onDeviceDB);
     *
     * // Register RAG store for external files
     * resolver.registerStorage(['page', 'excelread'], ragVectorStore);
     */
    registerStorage(prefixes, storage) {
        for (const prefix of prefixes) {
            this.storageByPrefix.set(prefix, storage);
            console.log(`[ContextResolver] Registered storage for prefix: ${prefix}`);
        }
    }
    /**
     * Get the storage backend for a prefix
     */
    getStorageForPrefix(prefix) {
        console.log(`[ContextResolver] Getting storage for prefix: ${prefix}`);
        console.log(`[ContextResolver] Storage by prefix:`, this.storageByPrefix);
        return this.storageByPrefix.get(prefix);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CONTENT STORAGE
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Store content and get a context ID
     *
     * @param prefix - The content type prefix
     * @param content - The content to store
     * @returns The generated context ID
     */
    store(prefix, content) {
        const contextId = generateContextID(prefix);
        const storage = this.getStorageForPrefix(prefix);
        if (storage) {
            storage.set(contextId, content);
            console.log(`[ContextResolver] Stored content with ID: ${contextId}`);
        }
        else {
            // Fallback to in-memory
            this.inMemoryStorage.set(contextId, content);
            console.log(`[ContextResolver] Stored content in memory (no registered storage): ${contextId}`);
        }
        return contextId;
    }
    /**
     * Store content with a specific ID (for re-storing or updating)
     *
     * Supports both simple prefixed IDs ("logo_abc123") and composite IDs
     * ("deck_abc:slide_257:shape_42"). For composite IDs, the content type
     * is extracted from the second segment for routing.
     */
    storeWithId(id, content) {
        // Register ID to prevent collision on future generates
        registerUsedId(id);
        const prefix = getContextIDPrefix(id);
        if (!prefix) {
            console.warn(`[ContextResolver] Invalid context ID format: ${id}`);
            this.inMemoryStorage.set(id, content);
            return;
        }
        const storage = this.getStorageForPrefix(prefix);
        if (storage) {
            storage.set(id, content);
        }
        else {
            this.inMemoryStorage.set(id, content);
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CONTENT RESOLUTION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Resolve a single context ID to its content
     *
     * Supports both simple prefixed IDs ("logo_abc123") and composite IDs
     * ("deck_abc:slide_257:shape_42"). For composite IDs, the content type
     * is extracted from the second segment for routing.
     *
     * For simple slide IDs ("slide_318"), automatically tries to find the
     * corresponding composite ID in the provided deck or current deck.
     *
     * @param id - The context ID to resolve
     * @param contextDocumentId - Optional deck ID from conversation context (for conversation-scoped resolution)
     * @returns Resolved content or null if not found
     */
    resolve(id, contextDocumentId) {
        const prefix = getContextIDPrefix(id);
        if (!prefix) {
            console.warn(`[ContextResolver] Invalid context ID format: ${id}`);
            return null;
        }
        // For composite IDs (e.g., d0|slide_325|read_complete), resolve to storage key
        // The LLM sees short aliases (d0|...) but data is stored with full deck ID
        let storageKey = id;
        if (isCompositeId(id)) {
            const resolved = resolveCompositeId(id);
            if (resolved) {
                storageKey = resolved.storageKey;
                console.log(`[ContextResolver] Resolved composite ID: ${id} → ${storageKey}`);
            }
            else {
                console.warn(`[ContextResolver] Failed to resolve composite ID: ${id}`);
            }
        }
        // NEW: For simple slide IDs, try to find in conversation's deck or current deck
        // This allows orchestrator to pass "slide_318" and have it resolve to the correct deck's data
        if (prefix === 'slide' && !isCompositeId(id)) {
            const deckRegistry = getAgentServices().contextProviders?.getDocumentRegistry?.();
            // ARCHITECTURE: Use contextDocumentId from conversation if provided (correct!)
            // Otherwise fall back to getCurrentDocumentId() from UI state (may be wrong for conversation)
            const documentIdToUse = contextDocumentId || deckRegistry.getCurrentDocumentId();
            if (!contextDocumentId && documentIdToUse) {
                console.warn(`[ContextResolver] ⚠️ No contextDocumentId provided for slide ID ${id}, falling back to UI current deck (${documentIdToUse.substring(0, 12)}...). This may resolve to wrong deck in multi-conversation scenarios.`);
            }
            if (documentIdToUse) {
                // Try common read variants in order of preference
                const variants = [
                    `${documentIdToUse}|${id}|read_complete`,
                    `${documentIdToUse}|${id}|read_layout`,
                    `${documentIdToUse}|${id}|read_styled`, // Legacy suffix support
                    `${documentIdToUse}|${id}`, // Without suffix
                ];
                const storage = this.getStorageForPrefix(prefix);
                for (const variant of variants) {
                    const content = storage?.get(variant);
                    if (content !== undefined) {
                        const source = contextDocumentId ? 'conversation-anchored' : 'UI-current';
                        console.log(`[ContextResolver] ✅ Resolved simple slide ID ${id} → ${variant} (${source} deck)`);
                        return {
                            id,
                            prefix,
                            content,
                            storedAt: this.inMemoryStorage.getStoredAt?.(variant),
                        };
                    }
                }
            }
            // Fall through to normal lookup if no composite variant found
            console.log(`[ContextResolver] No composite variant found for ${id}, trying simple lookup`);
        }
        const storage = this.getStorageForPrefix(prefix);
        const content = storage?.get(storageKey) ?? this.inMemoryStorage.get(storageKey);
        if (content === undefined) {
            console.warn(`[ContextResolver] Content not found for ID: ${id} (storageKey: ${storageKey})`);
            return null;
        }
        return {
            id,
            prefix,
            content,
            storedAt: this.inMemoryStorage.getStoredAt?.(storageKey),
        };
    }
    /**
     * Resolve multiple short IDs
     *
     * @param ids - Array of short IDs
     * @param contextDocumentId - Optional deck ID from conversation context
     * @returns Map of ID to resolved content (null for not found)
     */
    resolveMany(ids, contextDocumentId) {
        const results = new Map();
        for (const id of ids) {
            results.set(id, this.resolve(id, contextDocumentId));
        }
        return results;
    }
    /**
     * Resolve all IDs in context items
     *
     * @param items - Array of context items
     * @param contextDocumentId - Optional deck ID from conversation context
     * @returns Array of resolved context items with content
     */
    resolveContextItems(items, contextDocumentId) {
        return items.map(item => ({
            original: item,
            resolved: this.resolveMany(item.context, contextDocumentId),
        }));
    }
    /**
     * Check if a context ID exists
     */
    has(id) {
        const prefix = getContextIDPrefix(id);
        if (!prefix)
            return false;
        const storage = this.getStorageForPrefix(prefix);
        return storage?.has(id) ?? this.inMemoryStorage.has(id);
    }
    /**
     * Delete content by ID
     */
    delete(id) {
        const prefix = getContextIDPrefix(id);
        if (!prefix)
            return false;
        const storage = this.getStorageForPrefix(prefix);
        if (storage) {
            return storage.delete(id);
        }
        return this.inMemoryStorage.delete(id);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // IMAGE CACHE (for MessageBuilder integration)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Set the current model for image sizing decisions
     *
     * Call this before storing images to ensure proper sizing for the model.
     *
     * @param model - Model name (e.g., "anthropic/claude-3-5-sonnet")
     */
    // REMOVED: setCurrentModel, getCurrentModel - model config flows through GlobalStateManager
    /**
     * Store an image in the cache (sync version - no processing)
     *
     * Use this for images that are already properly sized, or when
     * async processing is not possible.
     *
     * @param id - Short ID for the image (logo_*, wikimedia_*, asset_*, blueprint_*)
     * @param base64 - Base64 encoded image data
     * @param mimeType - Image MIME type (e.g., 'image/png')
     * @param metadata - Optional metadata (should include width/height if known)
     */
    storeImage(id, base64, mimeType, metadata) {
        const detectedMimeType = detectImageMimeType(base64);
        if (detectedMimeType && detectedMimeType !== mimeType) {
            console.warn(`[ContextResolver] MIME mismatch for ${id}: ` +
                `provided=${mimeType}, detected=${detectedMimeType}. Using detected type.`);
            mimeType = detectedMimeType;
        }
        const enrichedMetadata = { ...metadata };
        // Normalize nested dimensions to top level
        // Some sources (Brandfetch, Wikimedia) store as { dimensions: { width, height } }
        // We normalize to top-level { width, height } for consistent access
        const dimensions = metadata?.dimensions;
        if (!enrichedMetadata.width && dimensions?.width) {
            enrichedMetadata.width = dimensions.width;
        }
        if (!enrichedMetadata.height && dimensions?.height) {
            enrichedMetadata.height = dimensions.height;
        }
        // Calculate tokens if dimensions are known
        if (enrichedMetadata.width && enrichedMetadata.height && !metadata?.estimatedTokens) {
            enrichedMetadata.estimatedTokens = estimateImageTokens(enrichedMetadata.width, enrichedMetadata.height);
        }
        this.imageCache.set(id, { base64, mimeType, metadata: enrichedMetadata });
        console.log(`[ContextResolver] Stored image: ${id}${enrichedMetadata.width ? ` (${enrichedMetadata.width}x${enrichedMetadata.height}, ~${enrichedMetadata.estimatedTokens} tokens)` : ''}`);
    }
    /**
     * Store an image with automatic resizing for optimal LLM performance
     *
     * This async version will:
     * 1. Load the image to get dimensions
     * 2. Resize if exceeds optimal limits (1568px, 1.15MP)
     * 3. Store with updated metadata
     *
     * @param id - Short ID for the image
     * @param base64 - Base64 encoded image data
     * @param mimeType - Image MIME type
     * @param metadata - Optional additional metadata
     * @returns Promise resolving to processed image info
     */
    async storeImageAsync(id, base64, mimeType, metadata) {
        try {
            const detectedMimeType = detectImageMimeType(base64);
            if (detectedMimeType && detectedMimeType !== mimeType) {
                console.warn(`[ContextResolver] MIME mismatch for ${id}: ` +
                    `provided=${mimeType}, detected=${detectedMimeType}. Using detected type.`);
                mimeType = detectedMimeType;
            }
            const imageCount = this.imageCache.size + 1;
            const processed = await processImage(base64, mimeType, this.currentModel, imageCount);
            const enrichedMetadata = {
                ...metadata,
                width: processed.width,
                height: processed.height,
                wasResized: processed.wasResized,
                estimatedTokens: processed.estimatedTokens,
            };
            if (processed.wasResized && metadata?.width && metadata?.height) {
                enrichedMetadata.originalWidth = metadata.width;
                enrichedMetadata.originalHeight = metadata.height;
            }
            this.imageCache.set(id, {
                base64: processed.base64,
                mimeType,
                metadata: enrichedMetadata
            });
            console.log(`[ContextResolver] Stored image: ${id} ` +
                `(${processed.width}x${processed.height}, ~${processed.estimatedTokens} tokens` +
                `${processed.wasResized ? ', resized' : ''})`);
            return processed;
        }
        catch (error) {
            console.error(`[ContextResolver] Error processing image ${id}:`, error);
            // Fallback to storing without processing
            this.storeImage(id, base64, mimeType, metadata);
            return {
                base64,
                width: 0,
                height: 0,
                wasResized: false,
                estimatedTokens: 0,
            };
        }
    }
    /**
     * Get an image from the cache
     */
    getImage(id) {
        return this.imageCache.get(id);
    }
    /**
     * Check if an image exists in cache
     */
    hasImage(id) {
        return this.imageCache.has(id);
    }
    /**
     * Delete an image from cache
     */
    deleteImage(id) {
        return this.imageCache.delete(id);
    }
    /**
     * Get all image IDs in cache
     */
    getImageIds() {
        return Array.from(this.imageCache.keys());
    }
    // REMOVED: getImageTokens, getTotalImageTokens, getImageCount - no external callers
    // REMOVED: parsePrefix, isValidId, getIdsByPrefix, getStorageType - no external callers
    // ═══════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Clear all in-memory content (session data)
     */
    clearInMemory() {
        this.inMemoryStorage.clear();
        console.log('[ContextResolver] Cleared in-memory storage');
    }
    /**
     * Clear the image cache
     */
    clearImageCache() {
        this.imageCache.clear();
        console.log('[ContextResolver] Cleared image cache');
    }
    /**
     * Clear everything (for testing)
     */
    clearAll() {
        this.inMemoryStorage.clear();
        this.imageCache.clear();
    }
}
// Export singleton getter for convenience
export const getContextResolver = () => ContextResolver.getInstance();
